import { Add } from '@mui/icons-material';
import { Box, Theme, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order } from '../../../apiTypes';
import OrderItemStatus from '../../../components/OrderItemStatus';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useLocale } from '../../../contexts/locale.context';
import StyledCard from '../../../QR/components/StyledCard';
import useMedia from '../../../utils/useMedia';
import EditDeliveryOrderButton from './EditDeliveryOrderButton';

type Props = {
  hasAddButton?: boolean;
  deliveryOrders: DbDocument<Order>[];
};

const DeliveryOrdersList = ({ hasAddButton, deliveryOrders }: Props) => {
  const { cafe, cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const navigate = useNavigate();

  const deliveryColors = useCallback(
    (t: Theme) => {
      if (deliveryOrders.some((o) => o.status === 'ordered')) {
        return { main: t.palette.primary.main, bg: `${t.palette.primary.main}22` };
      }

      if (deliveryOrders.some((o) => o.status === 'processing')) {
        return { main: t.palette.secondary.main, bg: `${t.palette.secondary.main}22` };
      }

      return { main: t.palette.success.main, bg: `${t.palette.success.main}22` };
    },
    [deliveryOrders]
  );

  if (!cafe?.qrMenu?.hasDelivery && !deliveryOrders.length) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isMobile ? 'row' : 'column',
          flexWrap: 'wrap',
          gap: 1,
          p: 1,
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: (t) => deliveryColors(t).main, width: '100%', textAlign: 'center' }}>
          {t.delivery[lang]}
        </Typography>

        {deliveryOrders.map((order) => (
          <StyledCard
            key={order.id}
            onClick={() => navigate(`/${cafeSlug}/pos/delivery?order=${order.id}`)}
            sx={{
              p: 1,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              border: order.status === 'ordered' ? (t) => `2px solid ${t.palette.primary.main}` : undefined,
            }}
          >
            {order.status && <OrderItemStatus status={order.status} />}
            <Typography sx={{ color: (t) => t.palette.text.secondary, mr: 1 }}>#{order.number}</Typography>
          </StyledCard>
        ))}

        {deliveryOrders.length === 0 && (
          <Typography
            sx={{ maxWidth: isMobile ? undefined : 100, textAlign: 'center' }}
            color={(t) => t.palette.text.secondary}
          >
            {t.there_is_nothing[lang]}
          </Typography>
        )}

        {cafe?.qrMenu?.hasDelivery && hasAddButton && (
          <EditDeliveryOrderButton fullWidth={!isMobile} variant="contained" color="success" startIcon={<Add />}>
            {t.add[lang]}
          </EditDeliveryOrderButton>
        )}
      </Box>
    </Box>
  );
};

export default DeliveryOrdersList;
